/**
 * Utilitaires de variantes — source unique pour identifier une combinaison
 * d'attributs et résoudre son image.
 *
 * `grouped_attributes` n'est que l'union des valeurs d'attributs du produit : il
 * décrit le produit cartésien couleur × pointure × mémoire, dont une partie
 * n'existe pas en base. Les combinaisons réellement créées viennent de
 * `GET /products/{id}?with_variants=true`, qui renvoie `variants[].values[]`.
 * Le SKU ne doit jamais servir à retrouver une combinaison : il est tronqué à
 * 4 caractères par valeur et suffixé aléatoirement côté API.
 */

import { variantPrice } from "./productPrice";

/** Clé stable d'une combinaison de valeurs d'attributs, indépendante de l'ordre. */
export const makeKey = (ids) => [...ids].sort().join("|");

/**
 * Indexe les variantes réellement existantes par clé de combinaison.
 *
 * @param {object}  product   Produit chargé avec `with_variants=true`
 * @param {boolean} isSample  true pour le côté échantillon, false pour le gros
 * @returns {Map<string, { valueIds: string[], variant: object }>}
 */
export function buildVariantIndex(product, isSample = false) {
  const index = new Map();
  const variants = Array.isArray(product?.variants) ? product.variants : [];

  variants.forEach((v) => {
    const side = isSample ? v.sample : v.normal;
    if (!side) return;
    const valueIds = (v.values || []).map((val) => val.id).filter(Boolean);
    if (valueIds.length === 0) return;

    index.set(makeKey(valueIds), {
      valueIds,
      values: v.values || [],
      // Même forme que la réponse de POST /products/{id}/variants
      variant: {
        id: v.id,
        sku: v.sku,
        stock: side.stock,
        sale_price: variantPrice(side),
        weight: side.weight,
        volume: side.volume,
        images: v.images || [],
        default_image: v.default_image || null,
        // Les valeurs voyagent avec la variante : sans elles, impossible de
        // retrouver la photo de sa couleur.
        values: v.values || [],
      },
    });
  });

  return index;
}

const imagePath = (image) => image?.path || image?.url || null;

/**
 * Images de la **galerie du produit** : `product.images` mélange trois ensembles
 * — la galerie, les photos des combinaisons (`product_variant_id`) et celles des
 * valeurs d'attributs (`product_attribute_value_id`).
 *
 * Les deux identifiants comptent : une photo de couleur a elle aussi
 * `product_variant_id` nul, et se ferait passer pour une image de galerie si on
 * ne testait que cette colonne — la vignette du produit deviendrait alors la
 * photo d'une couleur prise au hasard.
 */
const galleryImages = (product) =>
  (product?.images || []).filter(
    (image) => image && !image.product_variant_id && !image.product_attribute_value_id
  );

/**
 * Photo rattachée à une valeur d'attribut — typiquement une couleur.
 *
 * C'est ce qui permet à un produit de cent combinaisons de n'avoir que trois
 * photos : l'administrateur en dépose une par couleur, chaque combinaison
 * reprend celle de la sienne.
 */
const attributeValueImage = (product, valueId) => {
  if (!valueId) return null;

  const images = (product?.images || []).filter(
    (image) => image?.product_attribute_value_id === valueId
  );

  return images.find((image) => image.is_default) || images[0] || null;
};

/**
 * Image de la galerie du produit, et rien d'autre.
 *
 * `default_image` ne suffit pas : quand toutes les photos ont été déposées sur
 * les combinaisons, l'API y renvoie celle d'une variante — utilisable comme
 * vignette du produit, jamais comme repli d'une autre variante.
 */
export const resolveGalleryImagePath = (product) => {
  if (!product) return null;

  const gallery = galleryImages(product);
  const preferred = gallery.find((image) => image.is_default) || gallery[0];

  return imagePath(preferred);
};

/**
 * Toutes les images d'un produit, galerie d'abord puis photos des combinaisons.
 *
 * Pour les cartes qui font défiler plusieurs photos : la première doit être
 * celle du produit, pas celle d'une déclinaison prise au hasard.
 */
export const orderedProductImages = (product) => {
  const all = Array.isArray(product?.images) ? product.images.filter(Boolean) : [];

  if (all.length === 0) return product?.default_image ? [product.default_image] : [];

  const isGallery = (image) => !image.product_variant_id && !image.product_attribute_value_id;

  return [...all.filter(isGallery), ...all.filter((image) => !isGallery(image))];
};

/**
 * Résout le chemin d'image principal d'un produit, quelle que soit la forme de
 * la donnée (default_image, images[], ou champ image déjà normalisé).
 *
 * La galerie passe avant tout : une vignette de produit ne doit pas être la
 * photo d'une de ses combinaisons tant qu'il a une image à lui.
 */
export const resolveProductImagePath = (product) => {
  if (!product) return null;
  return (
    resolveGalleryImagePath(product) ||
    imagePath(product.default_image) ||
    imagePath(product.images?.[0]) ||
    (Array.isArray(product.image) ? product.image[0] : product.image) ||
    null
  );
};

/**
 * Image d'une combinaison, dans cet ordre : la sienne, celle de sa couleur,
 * puis la galerie du produit — **jamais celle d'une autre combinaison**. Sans
 * cette dernière restriction, une déclinaison blanche sans photo affichait
 * celle de la noire, parce que l'image du produit était en réalité celle d'une
 * variante.
 *
 * La reprise par couleur n'est pas un emprunt : la photo est explicitement
 * rattachée à une valeur que cette combinaison porte. « Blanc / 41 » affiche la
 * photo du blanc, jamais celle du noir.
 *
 * Le côté gros et le côté échantillon d'une même variante partagent l'image.
 */
export const resolveVariantImagePath = (variant, product) => {
  const own =
    imagePath(variant?.default_image) ||
    imagePath(variant?.images?.[0]);

  if (own) return own;

  for (const value of variant?.values || []) {
    const colour = imagePath(attributeValueImage(product, value?.id));
    if (colour) return colour;
  }

  return resolveGalleryImagePath(product);
};
